'use client'

import type { HeaderBehavior } from '@maestro/commerce-blocks/components/header/header.types'

import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import {
  STICKY_READY_CLASS,
  STICKY_SHOW_CLASS,
} from '@maestro/commerce-blocks/components/header/header.constants'
import { useIsHydrated } from '@maestro/commerce-blocks/hooks/use-is-hydrated'
import { theme } from '@maestro/local/theme'
import { useBreakpoint } from '@pearlconvert/ui/providers/breakpoint/breakpoint.context'
import { useDisabledPageScroll } from '@pearlconvert/ui/providers/disabled-page-scroll/disabled-page-scroll.context'

export const useHeader = () => {
  const [currentScrollY, setCurrentScrollY] = useState<number>(0)
  const lastScrollY = useRef(0)
  const [headerBehavior, setHeaderBehavior] =
    useState<HeaderBehavior>('relative')
  const headerRef = useRef<HTMLHeadingElement | null>(null)
  const { isDisabledPageScroll } = useDisabledPageScroll()
  const deltaUp = 8 // higher delta, less sensitive toggling behaviour
  const deltaDown = 2
  const [headerHeight, setHeaderHeight] = useState<number>(0)
  const { isMobileView, isDesktopView } = useBreakpoint()
  const isMobile = isMobileView()
  const isDesktop = isDesktopView()
  const isHydrated = useIsHydrated()
  const pathname = usePathname()
  const {
    site: { isContentOnly },
    header: { behavior, isTopbarEnabled },
  } = theme()

  useEffect(() => {
    if (isDisabledPageScroll) {
      return
    }

    const bodyClasses = document.body.classList

    if (headerBehavior === 'relative') {
      bodyClasses.remove(STICKY_READY_CLASS)
      bodyClasses.remove(STICKY_SHOW_CLASS)
    } else if (headerBehavior === 'fixed') {
      bodyClasses.add(STICKY_READY_CLASS)
      bodyClasses.add(STICKY_SHOW_CLASS)
    } else {
      if (currentScrollY <= 0) {
        bodyClasses.remove(STICKY_READY_CLASS)
        bodyClasses.remove(STICKY_SHOW_CLASS)
      } else if (currentScrollY > headerHeight * 2) {
        // If we're still close to the header we don't need to make it sticky
        bodyClasses.add(STICKY_READY_CLASS)
      }

      if (currentScrollY < lastScrollY.current - deltaUp) {
        // Scroll direction up
        if (document.body.classList.contains(STICKY_READY_CLASS)) {
          bodyClasses.add(STICKY_SHOW_CLASS)
        }
      } else if (currentScrollY > lastScrollY.current + deltaDown) {
        // Scroll direction down
        if (currentScrollY > headerHeight) {
          bodyClasses.remove(STICKY_SHOW_CLASS)
        }
      }
    }

    lastScrollY.current = currentScrollY
  }, [currentScrollY, headerBehavior, headerHeight, isDisabledPageScroll])

  useEffect(() => {
    if (isMobile) {
      setHeaderBehavior(behavior.mobile as HeaderBehavior)
    } else if (isDesktop) {
      setHeaderBehavior(behavior.desktop as HeaderBehavior)
    }
  }, [behavior.desktop, behavior.mobile, isDesktop, isMobile])

  useEffect(() => {
    if (isDisabledPageScroll) {
      return
    }
    const handleScroll = () => {
      setCurrentScrollY(window.scrollY)
    }

    const handleResize = () => {
      const height = headerRef.current?.offsetHeight || 0
      setHeaderHeight(height)
      document.body.style.setProperty(
        '--header__measured-height',
        height + 'px'
      )
    }

    // Defer the initial measurement (offsetHeight read) to the next frame so it
    // doesn't force a synchronous reflow during the mount commit. The measured
    // height only drives sticky-scroll offsets (with rem fallbacks in CSS) that
    // activate after the user scrolls, so a one-frame delay has no CLS impact.
    const rafId = requestAnimationFrame(handleResize)

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [isDisabledPageScroll])

  const handleSkipLinkKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
    }
  }

  return {
    handleSkipLinkKeyDown,
    headerRef,
    isContentOnly,
    isDesktop: isDesktop && isHydrated,
    isMobile,
    pathname,
    isTopbarEnabled,
  }
}
